import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Upload } from 'lucide-react';
import { createLocation, updateLocation } from '../../../services/locations';
import { uploadImage } from '../../../services/upload';
import './LocationForm.css';

const LocationForm = ({ location, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    size: '',
    capacity: '',
    price: '',
    description: '',
    amenities: [],
    images: [],
  });

  const [newAmenity, setNewAmenity] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (location) {
      setFormData(location);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddAmenity = () => {
    if (newAmenity.trim()) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, newAmenity.trim()]
      }));
      setNewAmenity('');
    }
  };

  const handleRemoveAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const handleAddImage = () => {
    if (newImageUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    const result = await uploadImage(file);

    if (result.success) {
      // Add the uploaded image URL to the images array
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, result.url]
      }));
    } else {
      alert('Upload failed: ' + (result.error || 'Unknown error'));
    }

    setUploading(false);
    
    // Reset file input
    e.target.value = '';
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    // Address is now optional
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.size.trim()) newErrors.size = 'Size is required';
    if (!formData.capacity.trim()) newErrors.capacity = 'Capacity is required';
    if (!formData.price.trim()) newErrors.price = 'Price is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    const result = location
      ? await updateLocation(location.id, formData)
      : await createLocation(formData);

    setLoading(false);

    if (result.success) {
      onSave();
      onClose();
    } else {
      alert('Error saving location: ' + result.error);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="location-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {location ? 'Edit Location' : 'Add New Location'}
          </h2>
          <button onClick={onClose} className="modal-close">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="location-form">
          <div className="form-scroll">
            {/* Basic Info */}
            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>
              
              <div className="form-group">
                <label className="form-label">Location Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Studio A"
                />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Size (sq ft) *</label>
                  <input
                    type="text"
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="5000 sq ft"
                  />
                  {errors.size && <span className="form-error">{errors.size}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Capacity *</label>
                  <input
                    type="text"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="50 people"
                  />
                  {errors.capacity && <span className="form-error">{errors.capacity}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Price ($/day) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="1500"
                  />
                  {errors.price && <span className="form-error">{errors.price}</span>}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="4"
                  placeholder="Describe the location..."
                />
              </div>
            </div>

            {/* Address */}
            <div className="form-section">
              <h3 className="form-section-title">Address</h3>
              
              <div className="form-group">
                <label className="form-label">Street Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="123 Main St (optional)"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Los Angeles"
                  />
                  {errors.city && <span className="form-error">{errors.city}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">State *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="CA"
                  />
                  {errors.state && <span className="form-error">{errors.state}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Zip Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="90028"
                  />
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="form-section">
              <h3 className="form-section-title">Amenities</h3>
              
              <div className="form-group">
                <div className="input-with-button">
                  <input
                    type="text"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAmenity())}
                    className="form-input"
                    placeholder="Add amenity (e.g., WiFi, Parking)"
                  />
                  <button
                    type="button"
                    onClick={handleAddAmenity}
                    className="btn btn-secondary btn-sm"
                  >
                    <Plus size={16} />
                    Add
                  </button>
                </div>
              </div>

              {formData.amenities.length > 0 && (
                <div className="tags-list">
                  {formData.amenities.map((amenity, index) => (
                    <div key={index} className="tag">
                      <span>{amenity}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAmenity(index)}
                        className="tag-remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Images */}
            <div className="form-section">
              <h3 className="form-section-title">Images</h3>
              
              {/* File Upload */}
              <div className="form-group">
                <label className="form-label">Upload Image</label>
                <div className="upload-container">
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="upload-input"
                    disabled={uploading}
                  />
                  <label htmlFor="image-upload" className="upload-label">
                    <Upload size={20} />
                    {uploading ? 'Uploading...' : 'Choose Image to Upload'}
                  </label>
                </div>
                <p className="form-help">
                  Upload images (JPG, PNG, GIF, WebP - Max 5MB each)
                </p>
              </div>

              {/* OR Image URL */}
              <div className="form-group">
                <label className="form-label">Or Add Image URL</label>
                <div className="input-with-button">
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddImage())}
                    className="form-input"
                    placeholder="https://example.com/image.jpg"
                  />
                  <button
                    type="button"
                    onClick={handleAddImage}
                    className="btn btn-secondary btn-sm"
                  >
                    <Plus size={16} />
                    Add URL
                  </button>
                </div>
              </div>

              {/* Image Previews */}
              {formData.images.length > 0 && (
                <div className="images-preview">
                  {formData.images.map((url, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={url} alt={`Location ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="image-remove"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (location ? 'Update Location' : 'Add Location')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationForm;